import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomButton } from '../../../components/buttons/customButton';
import { useOrganizationSettingsStore } from '../../../store/organizationSettingsStore';
import { useUpdateOnboardingStep } from '../../../apiHooks/mutations/onboarding/useUpdateOnboardingStep';
import useGetLatestFormSubmissions from '../../../apiHooks/queries/latestFormSubmissions/useGetLatestFormSubmissions';

interface VerifyStepProps {
  onContinue: () => void;
}

const TEST_AD_CODE = 'firstSubmission';

const VerifyStep: FC<VerifyStepProps> = ({ onContinue }) => {
  const { t } = useTranslation();
  const organizationSettings = useOrganizationSettingsStore((s) => s.organizationSettings);
  const { mutateAsync: updateStep, isLoading: isSaving } = useUpdateOnboardingStep();
  const { data, refetch, isRefetching } = useGetLatestFormSubmissions('onboarding-verify');

  const website = organizationSettings.website;
  const [copied, setCopied] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  const submissions = data?.data?.data?.data ?? [];
  const matchingSubmission = submissions.find((s) => s.adCode === TEST_AD_CODE);
  const verified = !!matchingSubmission;

  const testUrl = website
    ? `${website.startsWith('http') ? website : `https://${website}`}?dig=${TEST_AD_CODE}`
    : null;

  const handleCopy = async () => {
    if (!testUrl) return;
    try {
      await navigator.clipboard.writeText(testUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may not be available
    }
  };

  const handleCheck = async () => {
    setHasChecked(true);
    await refetch();
  };

  const handleContinue = async () => {
    await updateStep({ step: 'verify', status: 'completed' });
    onContinue();
  };

  if (!website) {
    return (
      <div className="flex w-full max-w-3xl flex-col gap-6">
        <div className="rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
          <p className="text-center text-sm text-gray-500">
            {t('setup.verify.noWebsite')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full max-w-3xl flex-col gap-6">
      {/* Header */}
      <div className="rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
        <h1 className="mb-2 text-center text-lg font-semibold text-gray-900">
          {t('setup.verify.title')}
        </h1>
        <p className="text-center text-sm leading-relaxed text-gray-500">
          {t('setup.verify.subtitle')}
        </p>
      </div>

      {/* Step 1 — Test Link */}
      <div className="rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
        <h2 className="mb-1.5 text-base font-semibold text-gray-900">
          {t('setup.verify.step1.title')}
        </h2>
        <p className="mb-5 text-sm leading-relaxed text-gray-500">
          {t('setup.verify.step1.description')}
        </p>

        {testUrl && (
          <div className="flex items-stretch gap-2">
            <a
              href={testUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex min-w-0 flex-1 items-center gap-2 rounded-lg bg-gray-50 px-4 py-3 transition-colors hover:bg-gray-100"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-primary">
                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              <span className="truncate text-sm font-medium text-primary">{testUrl}</span>
            </a>
            <button
              type="button"
              onClick={handleCopy}
              className="flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50"
            >
              {copied ? (
                <>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M2 7l3.5 3.5L12 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {t('setup.verify.step1.copySuccess')}
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                  </svg>
                  Copy
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Step 2 — Complete a submission */}
      <div className="rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
        <h2 className="mb-1.5 text-base font-semibold text-gray-900">
          {t('setup.verify.step2.title')}
        </h2>
        <p className="text-sm leading-relaxed text-gray-500">
          {t('setup.verify.step2.description')}
        </p>
      </div>

      {/* Step 3 — Check */}
      <div className="rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
        <h2 className="mb-1.5 text-base font-semibold text-gray-900">
          {t('setup.verify.step3.title')}
        </h2>
        <p className="mb-5 text-sm leading-relaxed text-gray-500">
          {t('setup.verify.step3.description')}
        </p>

        {verified && (
          <div className="mb-5 rounded-xl border-2 border-green bg-green/5 px-4 py-4">
            <div className="flex items-center gap-2.5">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M2 7l3.5 3.5L12 4" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span className="text-sm font-semibold text-green">{t('setup.verify.found')}</span>
            </div>
            <p className="mt-1.5 pl-[34px] text-sm leading-relaxed text-gray-500">
              {t('setup.verify.foundDescription')}
            </p>

            <div className="mt-4 pl-[34px]">
              <span className="mb-1 block text-xs font-medium text-gray-400">
                {t('setup.verify.submissionLabel')}
              </span>
              <div className="flex items-center gap-3 rounded-lg bg-white px-3 py-2 text-xs text-gray-600">
                <span className="font-mono">{matchingSubmission.adCode}</span>
                <span className="text-gray-300">|</span>
                <span>{matchingSubmission.path || '/'}</span>
                <span className="text-gray-300">|</span>
                <span>{new Date(matchingSubmission.createdAt).toLocaleString()}</span>
              </div>
            </div>
          </div>
        )}

        {hasChecked && !verified && !isRefetching && (
          <div className="mb-5 rounded-xl border-2 border-red-200 bg-red-50 px-4 py-4">
            <div className="flex items-center gap-2.5">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-500">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M4 4l6 6M10 4l-6 6" stroke="white" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
              <span className="text-sm font-semibold text-red-600">{t('setup.verify.notFound')}</span>
            </div>
            <p className="mt-1.5 pl-[34px] text-sm leading-relaxed text-gray-500">
              {t('setup.verify.notFoundDescription')}
            </p>
          </div>
        )}

        {!verified && (
          <CustomButton
            className="w-full justify-center py-2.5"
            disabled={isRefetching}
            onClick={handleCheck}
          >
            {isRefetching ? t('setup.verify.refreshing') : t('setup.verify.refreshButton')}
          </CustomButton>
        )}
      </div>

      {/* Continue */}
      {verified && (
        <CustomButton
          className="w-full justify-center py-2.5"
          disabled={isSaving}
          onClick={handleContinue}
        >
          {isSaving ? t('setup.saving') : `${t('setup.continue')} \u2192`}
        </CustomButton>
      )}
    </div>
  );
};

export default VerifyStep;
